/**
 * Drawer lateral con vista 360 consolidada del merchant.
 * Muestra cuentas, clientes asociados y actividad reciente.
 *
 * @taskId T468
 * @see functional/Specs/specs_cycle_4.md HU-26-FE §Pantalla 2
 */

import { useEffect, useRef } from 'react';
import { X, ExternalLink, CreditCard, Users, Activity } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button, Card, CardContent, CardHeader, CardTitle, Skeleton } from '@flexibilitypay/ui';

import { useMerchant360 } from '../hooks/use-merchants';
import type { AccountStatus, MerchantStatus } from '../types/merchant.types';

// ─── Status Badges ──────────────────────────────────────────────────────────

function MerchantStatusBadge({ status }: { status: MerchantStatus }) {
  const colorMap: Record<MerchantStatus, string> = {
    ACTIVE: 'bg-green-100 text-green-800',
    INACTIVE: 'bg-yellow-100 text-yellow-800',
    CLOSED: 'bg-red-100 text-red-800',
  };
  const labelMap: Record<MerchantStatus, string> = {
    ACTIVE: 'Activo',
    INACTIVE: 'Inactivo',
    CLOSED: 'Baja',
  };
  return (
    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${colorMap[status]}`}>
      {labelMap[status]}
    </span>
  );
}

function AccountStatusBadge({ status }: { status: AccountStatus }) {
  const colorMap: Record<AccountStatus, string> = {
    ACTIVE: 'bg-green-100 text-green-800',
    SUSPENDED: 'bg-yellow-100 text-yellow-800',
    CLOSED: 'bg-red-100 text-red-800',
    CREATED: 'bg-blue-100 text-blue-800',
  };
  const labelMap: Record<AccountStatus, string> = {
    ACTIVE: 'Activa',
    SUSPENDED: 'Suspendida',
    CLOSED: 'Cerrada',
    CREATED: 'Creada',
  };
  return (
    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${colorMap[status]}`}>
      {labelMap[status]}
    </span>
  );
}

// ─── Avatar ─────────────────────────────────────────────────────────────────

function MerchantAvatar({ razonSocial }: { razonSocial: string }) {
  const initials = razonSocial
    .split(' ')
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase();
  return (
    <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 text-xl font-semibold text-primary">
      {initials}
    </div>
  );
}

// ─── Formateo de montos (P-02) ──────────────────────────────────────────────

function formatMoney(amount: string): string {
  const num = parseFloat(amount);
  return new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS',
  }).format(num);
}

function formatCvu(cvu: string): string {
  // Formatear CVU para legibilidad: XXXX XXXX XXXX XXXX XXXX XX
  return cvu.replace(/(.{4})/g, '$1 ').trim();
}

// ─── Props ──────────────────────────────────────────────────────────────────

export interface MerchantDrawer360Props {
  merchantId: string | null;
  open: boolean;
  onClose: () => void;
  onViewHistory?: () => void;
}

// ─── Skeleton ───────────────────────────────────────────────────────────────

function DrawerSkeleton() {
  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center gap-4">
        <Skeleton className="h-16 w-16 rounded-full" />
        <div className="space-y-2">
          <Skeleton className="h-6 w-40" />
          <Skeleton className="h-4 w-24" />
        </div>
      </div>
      <div className="space-y-2">
        {[...Array(4)].map((_, i) => (
          <Skeleton key={i} className="h-4 w-full" />
        ))}
      </div>
      <Skeleton className="h-32 w-full rounded-lg" />
      <Skeleton className="h-24 w-full rounded-lg" />
    </div>
  );
}

// ─── Componente principal ───────────────────────────────────────────────────

export function MerchantDrawer360({
  merchantId,
  open,
  onClose,
  onViewHistory,
}: MerchantDrawer360Props) {
  const { t } = useTranslation('merchants');
  const drawerRef = useRef<HTMLDivElement>(null);

  const { data: merchant360, isLoading, error } = useMerchant360(merchantId ?? undefined);

  // Cerrar con Escape
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && open) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [open, onClose]);

  // Click fuera para cerrar
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (drawerRef.current && !drawerRef.current.contains(e.target as Node) && open) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <>
      {/* Overlay */}
      <div className="fixed inset-0 z-40 bg-black/50" aria-hidden="true" />

      {/* Drawer */}
      <div
        ref={drawerRef}
        className="fixed inset-y-0 right-0 z-50 w-full max-w-lg overflow-y-auto bg-background shadow-xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="drawer-title"
      >
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between border-b bg-background px-6 py-4">
          <h2 id="drawer-title" className="text-lg font-semibold">
            {t('drawer360.title', 'Vista 360')}
          </h2>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Content */}
        {isLoading ? (
          <DrawerSkeleton />
        ) : error ? (
          <div className="p-6">
            <p className="text-destructive">
              {t('drawer360.error', 'Error al cargar información del merchant')}
            </p>
            <Button variant="outline" size="sm" onClick={() => window.location.reload()} className="mt-4">
              {t('common:retry', 'Reintentar')}
            </Button>
          </div>
        ) : merchant360 ? (
          <div className="space-y-6 p-6">
            {/* Cabecera con avatar */}
            <div className="flex items-center gap-4">
              <MerchantAvatar razonSocial={merchant360.razonSocial} />
              <div>
                <h3 className="text-xl font-semibold">{merchant360.razonSocial}</h3>
                <MerchantStatusBadge status={merchant360.estado} />
              </div>
            </div>

            {/* Resumen del Merchant */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  {t('drawer360.summary', 'Resumen')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">CUIT</span>
                  <span className="font-mono">{merchant360.cuit}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Email</span>
                  <span>{merchant360.email}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Teléfono</span>
                  <span>{merchant360.telefono}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Domicilio</span>
                  <span className="text-right max-w-[200px]">{merchant360.domicilio}</span>
                </div>
              </CardContent>
            </Card>

            {/* Sección Cuentas */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                  <CreditCard className="h-4 w-4" />
                  {t('drawer360.accounts', 'Cuentas')} ({merchant360.cuentas.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {merchant360.cuentas.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    {t('drawer360.noAccounts', 'Sin cuentas asociadas')}
                  </p>
                ) : (
                  <div className="space-y-3">
                    {merchant360.cuentas.map((cuenta) => (
                      <div
                        key={cuenta.id}
                        className="flex items-center justify-between rounded-lg border p-3"
                      >
                        <div>
                          <p className="font-mono text-xs text-muted-foreground">
                            {formatCvu(cuenta.cvu)}
                          </p>
                          <p className="font-semibold">{formatMoney(cuenta.saldo)}</p>
                        </div>
                        <AccountStatusBadge status={cuenta.estado} />
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Sección Clientes Asociados */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                  <Users className="h-4 w-4" />
                  {t('drawer360.clients', 'Clientes Asociados')} ({merchant360.clientesAsociados.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {merchant360.clientesAsociados.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    {t('drawer360.noClients', 'Sin clientes asociados')}
                  </p>
                ) : (
                  <ul className="space-y-1">
                    {merchant360.clientesAsociados.map((cliente) => (
                      <li key={cliente.id} className="text-sm">
                        {cliente.nombre}
                        {cliente.tieneCvu && (
                          <span className="ml-2 text-xs text-green-600">✓ CVU</span>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>

            {/* Sección Actividad Reciente */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                  <Activity className="h-4 w-4" />
                  {t('drawer360.recentActivity', 'Actividad Reciente')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {merchant360.actividadReciente.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    {t('drawer360.noActivity', 'Sin actividad reciente')}
                  </p>
                ) : (
                  <>
                    <ul className="space-y-2">
                      {merchant360.actividadReciente.slice(0, 5).map((mov) => (
                        <li
                          key={mov.id}
                          className="flex items-center justify-between text-sm"
                        >
                          <div>
                            <span className="text-muted-foreground">
                              {new Date(mov.fecha).toLocaleDateString('es-AR')}
                            </span>
                          </div>
                          <span
                            className={
                              mov.tipo === 'CREDIT' ? 'text-green-600' : 'text-red-600'
                            }
                          >
                            {mov.tipo === 'CREDIT' ? '+' : '-'}
                            {formatMoney(mov.monto)}
                          </span>
                        </li>
                      ))}
                    </ul>
                    {onViewHistory && (
                      <Button
                        variant="link"
                        size="sm"
                        className="mt-3 p-0"
                        onClick={onViewHistory}
                      >
                        <ExternalLink className="mr-1 h-3 w-3" />
                        {t('drawer360.viewFullHistory', 'Ver Historial Completo')}
                      </Button>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        ) : null}
      </div>
    </>
  );
}
